/**
 * Person management: the record's Manage row, and the lifecycle Tasks it
 * visits. Archive changes only whether People lists the person; Offboard ends
 * exactly what its review read, then archives; Delete removes only a record
 * nothing depends on. Each Task offers the better depth when the facts it
 * reads call for it. Role and Add teaching profile live with the facts they
 * change (Access, Teaching) and are visited from here only by address.
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { useTabDraft } from "@/components/ui/box-draft";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import type { Blocker, Box, FactBox, Part, Read, TaskBox } from "@/components/ui/box.schema";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleDetailResponse, type PeopleManagementPreview, type PeopleMembershipRole } from "@/lib/api/people";

import { manageText as mt, manageWords as m, peopleText as text, peopleWords as words } from "../people-copy";
import { detailEligibleFacultyAccount, formatDay, membershipRoleLabel, statusLabel } from "../person-record";
import { teachingText } from "./teaching-copy";
import { resultState } from "./access-boxes";
import { PersonManageMenu, PersonVisitButton, ReviewGroup, type PersonManageChoice } from "./person-management-actions";
import { imam, queryRead, useTaskCommit, type PendingCommit } from "./commit";

export type PersonManagementJob = "archive" | "offboard" | "delete" | "role" | "teaching";

const commitWords = (verb: string) => ({
  refused: "The change was not made.",
  unknown: `${verb}: confirmation needed`,
  unknownSentence: "We could not confirm this change. Check its result before trying again.",
  checkNote: "Reads the saved result. Nothing is sent again.",
});
const asBox = <T,>(spec: TaskBox<T> | FactBox<T>) => spec as unknown as Box;
const effectsOf = (sentences: readonly string[]) => sentences.map((sentence, i) => ({ id: `effect-${i}`, sentence }));
const ROLE_WORD: Record<PeopleMembershipRole, string> = { student: "Student", instructor: "Instructor", admin: "Administrator" };
type Preview = PeopleManagementPreview;
type Assignment = Preview["teaching_assignments"][number];

/** A current primary whose course is published needs a teacher to take over; a draft's ends with none. */
const needsReplacement = (item: Assignment) => item.assignment_kind === "primary" && item.requires_replacement !== false;
/** Whether the review holds anything current that Offboard would end. */
const holdsCurrent = (checked: Preview) =>
  checked.enrollments.length > 0 || checked.teaching_assignments.length > 0 || checked.invitations.length > 0
  || Boolean(checked.student_standing?.will_withdraw) || Boolean(checked.access?.will_suspend);

export function useManagementBoxes(personId: string | null, detailRead: Read<PeopleDetailResponse>, job: string | null, lead?: ReactNode) {
  const client = useQueryClient();
  const { me } = useAuth();
  const detail = detailRead.status === "ready" ? detailRead.value : detailRead.status === "stale" ? detailRead.last : null;
  const person = detail?.person;
  const membership = detail?.identity_and_access?.membership;
  const archived = Boolean(person?.archived_at);
  const pid = personId ?? "none";
  const [deleted, setDeleted] = useState<string | null>(null);
  const offboardDraft = useTabDraft<{ note: string; replacements: Record<string, string> }, PendingCommit>({
    box: "person-offboard", record: pid, version: person?.updated_at ?? null, initial: { note: "", replacements: {} },
  });
  const roleDraft = useTabDraft<{ role: PeopleMembershipRole | "" }, PendingCommit>({
    box: "person-role", record: pid, version: membership?.role ?? null, initial: { role: "" },
  });
  const preview = useQuery({
    queryKey: [...PEOPLE_QUERY_KEY, "management", personId],
    queryFn: () => peopleApi.managementPreview(personId!),
    enabled: Boolean(personId && (job === "offboard" || job === "delete") && deleted !== personId),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["accounts"] }),
    client.invalidateQueries({ queryKey: ["students"] }),
    client.invalidateQueries({ queryKey: ["faculty"] }),
    client.invalidateQueries({ queryKey: ["courses"] }),
    client.invalidateQueries({ queryKey: ["invitations"] }),
  ]);
  const archive = useTaskCommit({ subject: `person:${pid}:archive`, generation: String(archived), words: commitWords("Archiving"), refresh });
  const role = useTaskCommit({ subject: `person:${pid}:role`, generation: membership?.role, words: commitWords("Changing role"), refresh, keep: roleDraft });
  const teachingDraft = useTabDraft<Record<string, never>, PendingCommit>({ box: "person-teaching-add", record: pid, version: null, initial: {} });
  const teaching = useTaskCommit({ subject: `person:${pid}:teaching`, words: commitWords("Adding teaching profile"), refresh, keep: teachingDraft });
  const offboard = useTaskCommit({ subject: `person:${pid}:offboard`, words: commitWords("Offboarding"), refresh, keep: offboardDraft });
  const remove = useTaskCommit({
    subject: `person:${pid}:delete`, words: commitWords("Deleting"),
    refresh: () => Promise.all([
      client.invalidateQueries({ queryKey: [...PEOPLE_QUERY_KEY, "register"] }),
      client.invalidateQueries({ queryKey: ["faculty"] }),
    ]),
    onReceipt: () => setDeleted(pid),
  });
  if (!personId || !person || !detail) return null;

  const id = (which: string) => `person-${pid}-${which}`;
  const refreshPart = (which: string, reset: () => void): Part => ({
    id: id(`${which}-refresh`), label: m.latestRecord, owner: imam, state: { class: "quiet", word: m.readAgain }, fold: "never", draft: "none",
    render: () => <Button variant="outline" size="sm" onClick={() => { reset(); void refresh(); }}>{m.refreshPerson}</Button>,
  });

  // Authority over this person's sign-in: never your own, never the tenant super admin's, and another
  // administrator's only with super authority. The same rule refuses Offboard and Change role on the server.
  const self = Boolean(membership) && detail.identity_and_access?.user_id === me?.user_id;
  const authority = !membership ? null
    : self ? m.roleSelfBlocked
      : membership.is_super_admin ? m.roleSuperAdminBlocked
        : membership.role === "admin" && !me?.is_super_admin ? m.roleAdminBlocked
          : null;
  const canChangeRole = Boolean(membership) && authority === null;
  const canOffboard = authority === null;
  const teachingAccount = detailEligibleFacultyAccount(detail);
  const canAddTeaching = !detail.faculty_profile && !archived;

  /* -------- The Manage row: Archived said once, and the lifecycle menu -------- */
  const choices: PersonManageChoice[] = [
    archived ? { job: "archive", label: m.restore, does: m.restoreDoes } : { job: "archive", label: m.archive, does: m.archiveDoes },
    ...(canAddTeaching ? [{ job: "teaching" as const, label: m.addTeaching, does: m.addTeachingDoes }] : []),
    ...(canOffboard ? [{ job: "offboard" as const, label: m.offboard, does: m.offboardDoes, destructive: true as const }] : []),
    { job: "delete", label: m.remove, does: m.removeDoes, destructive: true },
  ];
  const archivedOn = person.archived_at ? text.archivedOn(formatDay(person.archived_at)) : "";
  const summary = asBox<PeopleDetailResponse>({
    id: `person-actions-${pid}`, kind: "fact", label: m.label, job: m.job, owner: imam,
    read: detailRead, fold: "never", presentation: "embedded", detail: () => "",
    body: () => (
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 space-y-2">{archived && <p className="text-ui font-semibold text-foreground" data-person-archived="">{archivedOn}</p>}{lead}</div>
        <div className={lead ? "ms-auto flex justify-end" : undefined}><PersonManageMenu personId={pid} choices={choices} /></div>
      </div>
    ),
  });

  /* -------- Add teaching profile: one Task, from the menu or the Teaching section -------- */
  const teachingBox = asBox<PeopleDetailResponse>({
    id: id("teaching"), kind: "task", label: m.addTeaching, job: m.teachingJob, owner: imam,
    read: detailRead, fold: "choice", detail: () => teachingAccount ? teachingText.mayTeach(person.full_name ?? "") : m.teachingDetail,
    act: { does: "open", verb: m.addTeaching }, state: () => resultState(teaching.result, { class: teachingAccount ? "waiting" : "quiet", word: detail.faculty_profile ? m.profileExists : m.noProfile }),
    // An instructor who signs in without a profile is the record's attention (rank 1); anyone else adds one by choice.
    candidate: { eligible: canAddTeaching && teaching.result.state !== "confirmed", blocked: null, rank: teachingAccount ? 1 : null },
    parts: teaching.result.state === "refused" ? [refreshPart("teaching", teaching.reset)] : [],
    commit: { verb: m.addTeaching, cap: "When you add", effects: () => effectsOf(m.teachingEffects),
      blockers: (): Blocker[] => archived ? [{ kind: "missing", part: id("teaching"), sentence: words.restoreFirst }]
        : !person.full_name?.trim() ? [{ kind: "missing", part: id("teaching"), sentence: m.nameFirst }]
          : detail.faculty_profile ? [{ kind: "missing", part: id("teaching"), sentence: m.profileAlready }] : [],
      run: key => teaching.run(key, { effects: [...m.teachingEffects], write: async commitKey => { await peopleApi.addTeachingProfile(pid, person.updated_at, commitKey); } }),
    }, result: teaching.result, draft: false,
  });

  /* -------- Archive or Restore to People -------- */
  const teachingNow = (detail.teaching_assignments?.courses ?? []).filter(course => course.status !== "archived").length;
  const standingNow = detail.student_record && ["active", "leave_of_absence"].includes(detail.student_record.status) ? statusLabel(detail.student_record.status) : null;
  const inPlace = archived ? [] : [
    ...(membership?.is_active ? [m.canSignIn] : []),
    ...(teachingNow ? [mt.teachesSections(teachingNow)] : []),
    ...(standingNow ? [mt.standing(standingNow)] : []),
  ];
  const archiveEffects = archived ? m.restoreEffects : m.archiveEffects;
  const archiveBox = asBox<PeopleDetailResponse>({
    id: id("archive"), kind: "task", label: archived ? m.restore : m.archive, job: m.archiveJob, owner: imam,
    fold: "choice", read: detailRead, detail: () => archived ? archivedOn : m.archiveDetail,
    act: { does: "open", verb: archived ? m.restore : m.archive },
    state: () => resultState(archive.result, { class: "quiet", word: archived ? words.archivedWord : words.archivedPart }),
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [
      ...(inPlace.length ? [{
        id: id("archive-current"), label: m.stillInPlace, owner: imam, state: { class: "quiet" as const, word: m.unchanged }, fold: "never" as const, draft: "none" as const,
        render: () => <div className="grid gap-2 text-ui">
          <p>{m.stillInPlaceIntro}</p>
          <ul className="list-disc space-y-1 pl-5">{inPlace.map(line => <li key={line}>{line}</li>)}</ul>
          {canOffboard && <div><PersonVisitButton personId={pid} job="offboard" from={id("archive")} label={m.offboardInstead} instead /></div>}
        </div>,
      }] : []),
      ...(archive.result.state === "refused" ? [refreshPart("archive", archive.reset)] : []),
    ],
    commit: { verb: archived ? m.restore : m.archive, cap: "When you confirm", effects: () => effectsOf(archiveEffects), blockers: () => [],
      run: key => archive.run(key, { effects: [...archiveEffects], remains: [archived ? m.restoredRemains : m.archivedRemains], write: async commitKey => { await peopleApi.setArchived(pid, !archived, person.updated_at, commitKey); } }),
    }, result: archive.result, draft: false,
  });

  /* -------- Change role: with Access, beside the sign-in it changes -------- */
  const roleOptions = (["student", "instructor", ...(me?.is_super_admin ? ["admin" as const] : [])] as PeopleMembershipRole[])
    .filter(option => option !== membership?.role && (option !== "student" || detail.student_record));
  const chosenRole = roleDraft.value.role;
  const roleEffects = [chosenRole ? mt.roleEffect(ROLE_WORD[chosenRole], chosenRole === "admin") : "Change the LMS role for this same person", m.roleKept];
  const roleBox = asBox<PeopleDetailResponse>({
    id: id("role"), kind: "task", label: m.roleLabel, job: m.roleJob, owner: imam,
    fold: "choice", read: detailRead, detail: () => membership ? mt.currentRole(membershipRoleLabel(membership)) : "Invite this person to establish their sign-in role.",
    act: { does: "open", verb: m.roleLabel }, state: () => resultState(role.result, { class: "quiet", word: membership ? membershipRoleLabel(membership) : words.noSignIn }),
    candidate: { eligible: canChangeRole, blocked: null, rank: null },
    parts: [{ id: id("role-choice"), label: m.rolePart, owner: imam, state: { class: "quiet", word: chosenRole ? ROLE_WORD[chosenRole] : m.chooseRole }, fold: "never", draft: "browser", keep: roleDraft,
      render: () => <div className="grid gap-2"><Label htmlFor={id("role-select")}>{m.newRole}</Label><NativeSelect id={id("role-select")} value={chosenRole} disabled={!canChangeRole} onChange={event => { role.reset(); roleDraft.set({ role: event.target.value as PeopleMembershipRole }); }}>
        <option value="">{m.chooseRole}</option>{roleOptions.map(option => <option key={option} value={option}>{ROLE_WORD[option]}</option>)}
      </NativeSelect><p className="text-meta text-muted-foreground">{m.roleNote}</p>{role.result.state === "refused" && <div><Button variant="outline" size="sm" onClick={() => { role.reset(); void refresh(); }}>{m.refreshPerson}</Button></div>}</div>,
    }],
    commit: { verb: m.roleLabel, cap: m.roleCap, effects: () => effectsOf(roleEffects),
      blockers: (): Blocker[] => authority ? [{ kind: "missing", part: id("role-choice"), sentence: authority }]
        : !membership ? [{ kind: "missing", part: id("role-choice"), sentence: words.noSignIn }]
          : !chosenRole ? [{ kind: "missing", part: id("role-choice"), sentence: m.chooseNewRole }] : [],
      run: key => role.run(key, { effects: [m.roleChanged], write: async commitKey => { await peopleApi.changeRole(pid, chosenRole as PeopleMembershipRole, membership!.role, commitKey); } }),
    }, result: role.result, draft: roleDraft.kept,
  });

  /* -------- Offboard: the review, grouped by what it ends -------- */
  const previewRead = queryRead(preview, m.reviewUnavailable, m.reviewUnavailableDetail);
  const checked = preview.data;
  const primary = checked?.teaching_assignments.filter(needsReplacement) ?? [];
  const optionsFor = (courseId: string) => checked?.replacement_options.filter(option => option.course_ids.includes(courseId)) ?? [];
  const offboardEffects = checked ? [
    ...(checked.enrollments.length ? [mt.withdrawEnrollments(checked.enrollments.length)] : []),
    ...(primary.length ? [mt.handOn(primary.length)] : []),
    ...(checked.teaching_assignments.length - primary.length ? [mt.endTeaching(checked.teaching_assignments.length - primary.length)] : []),
    ...(checked.student_standing?.will_withdraw ? [m.standingEffect] : []),
    ...(checked.invitations.length ? [mt.revokeInvitations(checked.invitations.length)] : []),
    ...(checked.access?.will_suspend ? [m.suspendEffect] : []),
    archived ? m.keepArchived : m.archiveEffect,
    m.keepEffect,
  ] : [m.unreadEffect];
  const replacement = (item: Assignment) => {
    const options = optionsFor(item.course_id);
    return <div className="grid gap-1">
      <Label htmlFor={id(`replace-${item.id}`)}>{mt.replacementFor(item.label)}</Label>
      <NativeSelect id={id(`replace-${item.id}`)} value={offboardDraft.value.replacements[item.course_id] ?? ""} onChange={event => {
        offboard.reset(); offboardDraft.set(value => ({ ...value, replacements: { ...value.replacements, [item.course_id]: event.target.value } }));
      }}><option value="">{m.chooseReplacement}</option>{options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}</NativeSelect>
      {!options.length && <p className="text-meta text-muted-foreground">{m.noReplacement}</p>}
    </div>;
  };
  const review = (value: Preview) => {
    const standing = value.student_standing;
    const standingWord = standing ? statusLabel(standing.status) ?? standing.status : "";
    return <>
      {!holdsCurrent(value) && <p>{m.nothingCurrent}</p>}
      {value.teaching_assignments.length > 0 && <ReviewGroup label={m.teaching}>{value.teaching_assignments.map(item => <li key={item.id} className="grid gap-1.5">
        <span className="[overflow-wrap:anywhere]">{item.label}<span className="text-muted-foreground"> · {needsReplacement(item) ? m.replacePrimary : item.assignment_kind === "primary" ? m.draftPrimary : m.endsTeaching}</span></span>
        {needsReplacement(item) && replacement(item)}
      </li>)}</ReviewGroup>}
      {value.enrollments.length > 0 && <ReviewGroup label={m.courses}>{value.enrollments.map(item => <li key={item.id} className="[overflow-wrap:anywhere]">{item.label}<span className="text-muted-foreground"> · {m.enrollmentEnds}</span></li>)}</ReviewGroup>}
      {standing && <ReviewGroup label={m.standing}><li>{standing.will_withdraw ? mt.standingChanges(standingWord) : mt.standingStays(standingWord)}</li></ReviewGroup>}
      {value.invitations.length > 0 && <ReviewGroup label={m.invitations}>{value.invitations.map(item => <li key={item.id} className="[overflow-wrap:anywhere]">{item.label}<span className="text-muted-foreground"> · {m.revoked}</span></li>)}</ReviewGroup>}
      <ReviewGroup label={m.signIn}><li>{!value.access ? words.noSignIn : value.access.will_suspend ? m.signInWillSuspend : m.signInAlreadySuspended}</li></ReviewGroup>
      <p className="text-meta text-muted-foreground">{m.kept}</p>
      {value.offboard_blockers.map((blocker, index) => <p key={`${blocker.kind}-${index}`} role="status">{blocker.label}</p>)}
    </>;
  };
  const offboardBox = asBox<Preview>({
    id: id("offboard"), kind: "task", label: m.offboard, job: m.offboardJob, owner: imam,
    read: previewRead, fold: "choice", detail: () => m.offboardDetail,
    act: { does: "open", verb: m.reviewOffboarding }, state: () => resultState(offboard.result, { class: "quiet", word: m.reviewEffects }),
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [{ id: id("offboard-review"), label: m.reviewPart, owner: imam, state: { class: "quiet", word: m.review }, fold: "never", draft: "browser", keep: offboardDraft,
      render: () => <div className="grid gap-4 text-ui">
        {checked && review(checked)}
        <div><Button type="button" variant="outline" size="sm" onClick={() => { offboard.reset(); void preview.refetch(); }}>{m.refreshReview}</Button></div>
        <div className="grid gap-1.5">
          <Label htmlFor={id("offboard-note")}>{m.reason}</Label>
          <Textarea id={id("offboard-note")} value={offboardDraft.value.note} onChange={event => { offboard.reset(); offboardDraft.set(value => ({ ...value, note: event.target.value })); }} />
        </div>
        {checked?.access?.will_suspend && <p className="text-meta text-muted-foreground">{m.suspendNow}</p>}
      </div>,
    }],
    commit: { verb: m.offboard, cap: m.offboardCap, effects: () => effectsOf(offboardEffects),
      blockers: (): Blocker[] => [
        ...(!checked || !checked.can_offboard ? [{ kind: "missing" as const, part: id("offboard-review"), sentence: checked?.offboard_blockers.map(item => item.label).join(" ") || m.readReviewFirst }] : []),
        ...(primary.some(item => !offboardDraft.value.replacements[item.course_id]) ? [{ kind: "missing" as const, part: id("offboard-review"), sentence: m.replaceEach }] : []),
        ...(!offboardDraft.value.note.trim() ? [{ kind: "missing" as const, part: id("offboard-review"), sentence: m.enterReason }] : []),
      ],
      run: key => offboard.run(key, { effects: offboardEffects, write: async commitKey => {
        const read = checked!;
        const answer = await peopleApi.offboard(pid, { expected_updated_at: read.updated_at, fingerprint: read.fingerprint, replacements: offboardDraft.value.replacements, note: offboardDraft.value.note.trim() }, commitKey);
        const { enrollments, teaching_assignments: taught, invitations } = answer.affected;
        return offboard.confirmed([
          m.offboarded,
          ...(enrollments ? [mt.enrollmentsWithdrawn(enrollments)] : []),
          ...(taught ? [mt.teachingHandled(taught)] : []),
          ...(invitations ? [mt.invitationsRevoked(invitations)] : []),
          ...(read.student_standing?.will_withdraw ? [m.standingWithdrawn] : []),
          ...(answer.access_suspended ? [read.access?.will_suspend ? m.signInSuspendedLine : m.signInStaysSuspended] : []),
          m.archivedLine,
        ], [mt.recovery({ signIn: Boolean(read.access), standing: Boolean(read.student_standing?.will_withdraw), courses: enrollments > 0 || taught > 0 })]);
      } }),
    }, result: offboard.result, draft: offboardDraft.kept,
  });

  /* -------- Delete: only a record nothing depends on -------- */
  const deleteBox = asBox<Preview>({
    id: id("delete"), kind: "task", label: m.remove, job: m.deleteJob, owner: imam,
    read: deleted === pid ? { status: "ready", value: checked!, readAt: new Date().toISOString() } : previewRead,
    fold: "choice", detail: () => m.deleteDetail,
    act: { does: "open", verb: m.reviewDeletion }, state: () => resultState(remove.result, { class: "quiet", word: checked?.can_delete ? m.unused : m.reviewDependencies }),
    candidate: { eligible: deleted !== pid, blocked: null, rank: null },
    parts: [{ id: id("delete-review"), label: m.deletePart, owner: imam, state: { class: "quiet", word: checked?.can_delete ? m.readyToDelete : m.review }, fold: "never", draft: "none",
      render: () => <div className="grid gap-3 text-ui">
        {checked?.delete_blockers.length ? <>
          <ul className="list-disc space-y-1 pl-5">{checked.delete_blockers.map((blocker, index) => <li key={`${blocker.kind}-${index}`}>{blocker.label}</li>)}</ul>
          {!archived && <p>{m.archiveWhileRetained}</p>}
          {canOffboard && checked.can_offboard && holdsCurrent(checked) && <p>{m.offboardWhileCurrent}</p>}
          <div className="flex flex-wrap gap-2">
            {!archived && <PersonVisitButton personId={pid} job="archive" from={id("delete")} label={m.archiveInstead} instead />}
            {canOffboard && checked.can_offboard && holdsCurrent(checked) && <PersonVisitButton personId={pid} job="offboard" from={id("delete")} label={m.offboardInstead} instead />}
          </div>
        </> : <p>{m.nothingRetained}</p>}
        <div><Button variant="outline" size="sm" onClick={() => void preview.refetch()}>{m.refreshReview}</Button></div>
      </div>,
    }],
    commit: { verb: m.deleteVerb, cap: m.deleteCap, effects: () => effectsOf(m.deleteEffects),
      blockers: () => checked?.can_delete ? [] : [{ kind: "missing", part: id("delete-review"), sentence: checked ? m.mustRetain : m.readDeletionFirst }],
      run: key => remove.run(key, { effects: [...m.deleted], write: async commitKey => { await peopleApi.remove(pid, checked!.updated_at, commitKey); setDeleted(pid); } }),
    }, result: remove.result, draft: false,
  });
  return {
    summary,
    boxes: { archive: archiveBox, role: roleBox, offboard: offboardBox, delete: deleteBox, teaching: teachingBox },
    canChangeRole,
    /** An instructor who signs in without a teaching profile: Add teaching profile is the Teaching section's attention. */
    teachingAttention: (Boolean(teachingAccount) && !archived) || teaching.result.state !== "none",
    deleted: deleted === pid,
  };
}
